import { ReturnProduct } from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { CountryCode } from "@redotech/shopify-client/storefront.graphql";
import { weightUnitToGrams } from "@redotech/shopify-client/utils";
import { Gid } from "@redotech/shopify-util/id";
import { filterTruthy } from "@redotech/util/array";
import { asyncIterableToArray } from "@redotech/util/async-iterator";
import { chunks } from "@redotech/util/iterator";
import {
  mockGetCollectionProductsPaginatedQueryResponse,
  mockGetCollectionProductsPaginatedWithContextQueryResponse,
} from "./mock-responses/collections";
import { ProductInfo } from "./products";
import {
  GetAllCollectionsQuery,
  GetAllCollectionsQueryVariables,
  GetCollectionProductsPaginatedQuery,
  GetCollectionProductsPaginatedQueryVariables,
  GetCollectionProductsPaginatedWithContextQuery,
  GetCollectionProductsPaginatedWithContextQueryVariables,
  GetCollectionsByGidsQuery,
  GetCollectionsByGidsQueryVariables,
} from "./shopify-storefront.graphql";
import {
  handleErrors,
  ShopifyStorefrontClientInterface,
} from "./storefront-client";

const getCollectionProductsPaginatedQuery = /* GraphQL */ `
  query GetCollectionProductsPaginated(
    $collectionID: ID!
    $first: Int!
    $after: String
  ) {
    collection(id: $collectionID) {
      products(first: $first, after: $after) {
        nodes {
          id
          title
          tags
          productType
          options {
            id
            name
            values
          }
          featuredImage {
            altText
            url
          }
          variants(first: 100) {
            nodes {
              id
              sku
              title
              weight
              weightUnit
              quantityAvailable
              selectedOptions {
                name
                value
              }
              price {
                amount
                currencyCode
              }
              image {
                url
              }
            }
          }
          collections(first: 100) {
            nodes {
              title
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

export const productInfoToReturnProduct = (
  product: ProductInfo,
): ReturnProduct | null => {
  const primaryVariant = product.variants?.nodes[0];
  if (!primaryVariant) {
    return null;
  }
  return {
    id: primaryVariant?.id,
    title: product.title,
    variantTitle: primaryVariant.title,
    variantId: primaryVariant.id,
    image: product.featuredImage?.url ?? "",
    price: primaryVariant.price.amount,
    currency: primaryVariant.price.currencyCode,
    productTags: product.tags,
    returnValue: primaryVariant.price.amount,
    sku: primaryVariant.sku ?? "",
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    productId: product.id.split("/").slice(-1)[0],
    deliveredOn: new Date(),
    fulfilledOn: new Date(),
    lastFulfillmentUpdateOn: new Date(),
    productDiscounts: [],
    productDiscountedAmount: 0,
    variantData: {
      productTitle: product.title,
      tags: product.tags,
      options: product.options.map((option, index) => ({
        position: index,
        name: option.name,
        values: option.values,
      })),
      variants: product.variants.nodes.map((variant) => ({
        id: variant.id,
        sku: variant.sku,
        title: variant.title,
        displayName: variant.title,
        price: variant.price.amount,
        image: variant.image?.url,
        selectedOptions: variant.selectedOptions,
        inventoryTracked: false,
        inventoryQuantity: variant.quantityAvailable ?? 0,
        inventoryPolicy:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          "DENY" as ReturnProduct["variantData"]["variants"][0]["inventoryPolicy"],
      })),
    },
    productType: product.productType,
    collections: product.collections.nodes.map(
      (collection) => collection.title,
    ),
    productVariantMetafields: {},
    grams:
      weightUnitToGrams({
        unit: primaryVariant.weightUnit,
        value: primaryVariant.weight,
      }) ?? 1,
    tax: "0",
  };
};

export async function getCollectionProductsPaginated(
  client: ShopifyStorefrontClientInterface,
  {
    collectionId,
    cursor,
    pageSize,
  }: { collectionId: string; cursor: string | undefined; pageSize: number },
): Promise<{
  products: ReturnProduct[];
  cursor: string | undefined;
  hasNextPage: boolean;
}> {
  const { data, errors } = await client.request<
    GetCollectionProductsPaginatedQuery,
    GetCollectionProductsPaginatedQueryVariables
  >(
    getCollectionProductsPaginatedQuery,
    { collectionID: collectionId, first: pageSize, after: cursor },
    mockGetCollectionProductsPaginatedQueryResponse,
  );

  handleErrors(errors);

  const pageInfo = data?.collection?.products?.pageInfo;
  const products = (data?.collection?.products?.nodes ?? []).reduce<
    ReturnProduct[]
  >((acc, product) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const returnProduct = productInfoToReturnProduct(product as ProductInfo);
    if (!returnProduct) {
      return acc;
    }
    return [...acc, returnProduct];
  }, []);

  return {
    products,
    cursor: pageInfo?.endCursor ?? undefined,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
}

const getCollectionProductsPaginatedWithContextQuery = /* GraphQL */ `
  query GetCollectionProductsPaginatedWithContext(
    $collectionID: ID!
    $first: Int!
    $after: String
    $country: CountryCode!
  ) @inContext(country: $country) {
    collection(id: $collectionID) {
      products(first: $first, after: $after) {
        nodes {
          id
          title
          tags
          productType
          options {
            id
            name
            values
          }
          featuredImage {
            altText
            url
          }
          variants(first: 100) {
            nodes {
              id
              sku
              title
              weight
              weightUnit
              quantityAvailable
              selectedOptions {
                name
                value
              }
              price {
                amount
                currencyCode
              }
              image {
                url
              }
            }
          }
          collections(first: 100) {
            nodes {
              title
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

export async function getCollectionProductsPaginatedWithContext(
  client: ShopifyStorefrontClientInterface,
  {
    collectionId,
    cursor,
    pageSize,
    countryCode,
  }: {
    collectionId: string;
    cursor: string | undefined;
    pageSize: number;
    countryCode?: string;
  },
): Promise<{
  products: ReturnProduct[];
  cursor: string | undefined;
  hasNextPage: boolean;
}> {
  if (!countryCode) {
    return getCollectionProductsPaginated(client, {
      collectionId,
      cursor,
      pageSize,
    });
  }

  const { data, errors } = await client.request<
    GetCollectionProductsPaginatedWithContextQuery,
    GetCollectionProductsPaginatedWithContextQueryVariables
  >(
    getCollectionProductsPaginatedWithContextQuery,
    {
      collectionID: collectionId,
      first: pageSize,
      after: cursor,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      country: countryCode as CountryCode,
    },
    mockGetCollectionProductsPaginatedWithContextQueryResponse,
  );

  handleErrors(errors);

  const pageInfo = data?.collection?.products?.pageInfo;
  const products = (data?.collection?.products?.nodes ?? []).reduce<
    ReturnProduct[]
  >((acc, product) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const returnProduct = productInfoToReturnProduct(product as ProductInfo);
    if (!returnProduct) {
      return acc;
    }
    return [...acc, returnProduct];
  }, []);

  return {
    products,
    cursor: pageInfo?.endCursor ?? undefined,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
}

const getCollectionsByGidsQuery = /* GraphQL */ `
  query getCollectionsByGids($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Collection {
        id
        title
      }
    }
  }
`;

export type MaybeCollectionInfo = GetCollectionsByGidsQuery["nodes"][number];
export type CollectionInfo = NonNullable<MaybeCollectionInfo>;

export async function getCollectionsByGids(
  client: ShopifyStorefrontClientInterface,
  ids: Gid[],
): Promise<CollectionInfo[]> {
  const MAX_BATCH_SIZE = 250;
  const results: CollectionInfo[] = [];

  // Process ids in batches of 250 as Shopify has a limit of 250 ids per request
  for (const batchIds of chunks(ids, MAX_BATCH_SIZE)) {
    const { data, errors } = await client.request<
      GetCollectionsByGidsQuery,
      GetCollectionsByGidsQueryVariables
    >(getCollectionsByGidsQuery, { ids: batchIds });

    handleErrors(errors);

    results.push(...filterTruthy(data?.nodes ?? []));
  }

  return results;
}

const getAllCollectionsQuery = /* GraphQL */ `
  query GetAllCollections($after: String) {
    collections(first: 100, after: $after) {
      edges {
        node {
          id
          title
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

async function* getAllCollectionsIterator(
  client: ShopifyStorefrontClientInterface,
) {
  let after: string | undefined = undefined;

  while (true) {
    const { data, errors } = await client.request<
      GetAllCollectionsQuery,
      GetAllCollectionsQueryVariables
    >(getAllCollectionsQuery, { after });

    handleErrors(errors);

    if (!data?.collections?.edges) {
      break;
    }

    for (const edge of data.collections.edges) {
      yield edge.node;
    }

    if (!data.collections.pageInfo.hasNextPage) {
      break;
    }

    after = (data.collections.pageInfo.endCursor ?? undefined) as
      | string
      | undefined;
  }
}

export async function getAllCollections(
  client: ShopifyStorefrontClientInterface,
): Promise<CollectionInfo[]> {
  return asyncIterableToArray(getAllCollectionsIterator(client));
}
